import log, { LogLevelDesc } from 'loglevel';
import * as React from 'react';
import { forwardRef, RefObject, useEffect, useRef } from 'react';
import type { Store } from 'redux';
import { Provider } from 'react-redux';
import { Map } from 'immutable';
import { JovoSocketIoSocket } from './socket-impl/jovo-socketio';
import { createNoopSocket } from './socket-impl/noop-socket';
import Widget from './components/Widget';
import TasksManager from './components/TasksManager';
import AuthtokenManager, { GetToken, InvalidateToken } from './components/AuthtokenManager';
import { initStore } from '../src/store/store';
import ThemeContext from '../src/components/Widget/ThemeContext';
import { loadTheme, applyCSSVariables } from './themes/theme-provider';
import { SESSION_NAME } from './constants';
import { BrowserStorage } from './browser-storage';
import {
    configureHttpBackendUrl,
    configureSocketUrl,
    openChat,
    closeChat,
    toggleChat,
    setAuthtoken,
    setBrowserLocation,
    setCustomerId,
    setGreetingsDelay,
    setUserId,
    setUserInfoExists,
    triggerMessageDelayed,
    setAnalyticsServiceUrl,
    setSessionId,
} from './store/actions';
import { APPLICATION_EVENT } from './application-events';
import { I18nextProvider, useTranslation } from 'react-i18next';
import i18next from './i18n/i18n';
import { setAnalyticsPartnerId } from './api/analytics-client';

export type ConnectedWidgetProps = {
    initPayload?: string;
    title?: string | React.ElementType;
    subtitle?: string | React.ElementType;
    protocol?: string;
    socketUrl?: string;
    socketPath?: string;
    httpBackendUrl?: string;
    protocolOptions?: any;
    jovoWebhook?: string;
    customData?: any;
    handleNewUserMessage?: Function;
    inputTextFieldHint?: string;
    connectingText?: string;
    showCloseButton?: boolean;
    showFullScreenButton?: boolean;
    hideWhenNotConnected?: boolean;
    connectOn?: string;
    autoClearCache?: boolean;
    onSocketEvent?: Record<string, Function>;
    badge?: number;
    embedded?: boolean;
    params?: any;
    openLauncherImage?: string;
    closeImage?: string;
    docViewer?: boolean;
    customComponent?: Function;
    displayUnreadCount?: boolean;
    showMessageDate?: boolean | Function;
    customMessageDelay?: Function;
    tooltipPayload?: string;
    tooltipDelay?: number;
    onWidgetEvent?: any;
    disableTooltips?: boolean;
    defaultHighlightClassname?: string;
    defaultHighlightCss?: string;
    defaultHighlightAnimation?: string;
    getToken?: GetToken;
    getTokenIntervalMs?: number;
    invalidateToken?: InvalidateToken;
    browserLocation?: any;
    navigateCallbacks?: any;
    welcomeMessages?: Array<string>;
    launchIntent?: boolean;
    loglevel?: string;
    styleTheme?: any;
    launcherGreetingsDelayMs?: number;
    fetchTasksRetryCount?: number;
    locationChangeEventsActiveSeconds?: number; // how many seconds after last interaction widget should continue sending location change events
    deeplink?: string;
    userInfo?: any;
    language?: string;
    analyticsServiceUrl?: string;
    partnerId?: string;
    overlayMode?: boolean;
    chatWindowInsetX?: string;
    chatWindowInsetY?: string;
    disablePreviewBubble?: boolean;
    pageUrl?: string;
    analyticsReferrer?: string;
    mobileFullScreenOnFocus?: boolean;
};

const ensureMessageHandler = (ref: RefObject<any>, messageName: string, handler: Function) => {
    if (ref && ref.current) {
        ref.current.off(messageName);
        ref.current.on(messageName, handler);
    }
};

const ensureMessageHandlers = (
    props: any,
    store: Store,
    browserStorage: BrowserStorage,
    socketRef: RefObject<any>
) => {
    const requestAuthtokenHandler = async ({ _responseId }: { _responseId: string }) => {
        log.debug('received request_authtoken message from server', _responseId);

        const session_id = browserStorage.getIn([SESSION_NAME, 'session_id']);

        if (session_id && socketRef && socketRef.current) {
            const authtoken = props.getToken && (await props.getToken());

            store && store.dispatch(setAuthtoken(authtoken));

            const responseId = _responseId || 'authtoken';
            socketRef.current.emit(responseId, { session_id, authtoken });
        }
    };

    const invalidateAuthtokenHandler = async () => {
        log.debug('received invalidate_authtoken message from server');

        props.invalidateToken && (await props.invalidateToken());
    };

    const requestHistoryHandler = async (...args: any[]) => {
        log.debug('received request_chathistory message from server', args);

        const chathistory = (
            (browserStorage.getIn([SESSION_NAME, 'conversation']) || []) as Array<any>
        )
            .filter(({ hidden }) => !hidden)
            .map(({ type, text, sender, timestamp }) => ({ type, text, sender, timestamp }));

        socketRef && socketRef.current && socketRef.current.emit('chathistory', { chathistory });
    };

    const setTypingIndicatorHandler = ({ enable }: { enable: boolean }) => {
        log.debug('received set_typing_indicator message from server', enable);

        store && store.dispatch(triggerMessageDelayed(enable !== undefined ? enable : true));
    };

    ensureMessageHandler(socketRef, 'request_authtoken', requestAuthtokenHandler);
    ensureMessageHandler(socketRef, 'invalidate_authtoken', invalidateAuthtokenHandler);
    ensureMessageHandler(socketRef, 'request_chathistory', requestHistoryHandler);
    ensureMessageHandler(socketRef, 'set_typing_indicator', setTypingIndicatorHandler);
};

const ConnectedWidget = forwardRef<any, ConnectedWidgetProps>(
    (props: ConnectedWidgetProps, ref: any) => {
        const instanceSocket = useRef<any>(createNoopSocket());
        const store = useRef<any>(null);

        React.useImperativeHandle(
            ref,
            () => ({
                open: () => store.current && store.current.dispatch(openChat()),
                close: () => store.current && store.current.dispatch(closeChat()),
                toggle: () => store.current && store.current.dispatch(toggleChat()),
            }),
            []
        );

        const location = useRef<any>(props && props.browserLocation);
        const navigateCallbacks = useRef<any>(props && props.navigateCallbacks);
        navigateCallbacks.current = props && props.navigateCallbacks;

        const { t } = useTranslation();

        const initSocketIoSocket = (store: RefObject<any>, browserStorage: BrowserStorage) => {
            if (!instanceSocket.current || !instanceSocket.current.url) {
                if (store && store.current && store.current.socketRef) {
                    log.debug('setting instanceSockett.current to existing socket');

                    instanceSocket.current = store.current.socket;
                } else {
                    log.debug('setting instanceSocket.current to newly created socket instance');

                    instanceSocket.current = new JovoSocketIoSocket(
                        props.socketUrl,
                        {
                            ...props.customData,
                            ...(props.partnerId ? { partnerId: props.partnerId } : {}),
                        },
                        props.socketPath,
                        props.protocol,
                        props.protocolOptions,
                        props.onSocketEvent,
                        browserStorage,
                        () => location.current && location.current.pathname,
                        [
                            {
                                // don't emit launch_intent if we've been triggered by deeplink:
                                superior: 'deeplink',
                                inferior: 'launch_intent',
                            },
                        ],
                        ['deeplink', 'launch_intent'],
                        props.onWidgetEvent && props.onWidgetEvent.onMessageEmit,
                        () => i18next.language
                    );

                    instanceSocket.current.on(
                        'connect',
                        () =>
                            props.onWidgetEvent &&
                            props.onWidgetEvent.onConnect &&
                            props.onWidgetEvent.onConnect()
                    );
                    instanceSocket.current.on(
                        'disconnect',
                        () =>
                            props.onWidgetEvent &&
                            props.onWidgetEvent.onDisconnect &&
                            props.onWidgetEvent.onDisconnect()
                    );
                    instanceSocket.current.on(
                        'session_confirm',
                        () =>
                            props.onWidgetEvent &&
                            props.onWidgetEvent.onSessionConfirmed &&
                            props.onWidgetEvent.onSessionConfirmed()
                    );
                }
            } else {
                log.debug('instanceSocket.current does already exist, no need to reset it');
            }
        };

        const getSecondsSinceLastInteraction = () => {
            const state =
                store && store.current && store.current.getState && store.current.getState();
            const lastMessageTimestamp =
                state &&
                state.messages &&
                state.messages.reduce(
                    (ts: number, msg: Map<string, any>) =>
                        msg.get('timestamp', 0) > ts ? msg.get('timestamp', 0) : ts,
                    0
                );

            return Math.floor((new Date().getTime() - (lastMessageTimestamp || 0)) / 1000);
        };

        const navigateAppWeb = (urlName: string, urlParams: any) => {
            // log.debug('got navigate call', urlName, urlParams, navigateCallbacks.current);

            if (urlName && navigateCallbacks.current) {
                const navigateCallback =
                    navigateCallbacks.current && navigateCallbacks.current[urlName];
                navigateCallback && navigateCallback();
            }
        };

        const emitApplicationEvent = (event: string, payload: any) => {
            const session_id = browserStorage.getIn([SESSION_NAME, 'session_id']);

            // log.debug('EMIT APPLICATION EVENT', { session_id, socket: instanceSocket.current, event, payload });

            if (event && session_id && instanceSocket.current) {
                instanceSocket.current.emit('application_event', {
                    session_id,
                    event: {
                        type: event,
                        data: payload,
                    },
                });
            }
        };

        log.setLevel((props.loglevel as LogLevelDesc) || 'silent');

        const storage = props.params.storage === 'session' ? sessionStorage : localStorage;
        const browserStorage = new BrowserStorage(storage);

        if (props.socketUrl) {
            initSocketIoSocket(store, browserStorage);
        }

        useEffect(() => {
            if (!props.socketUrl) {
                // Deliberately console.error, not log.error: the default
                // loglevel is silent and a misconfigured host page must be
                // able to see why the chat never connects.
                console.error(
                    '[CUI Widget] no socketUrl configured — the widget renders but cannot connect. Set socketUrl (and socketPath) in window.CUIWidgetConfig.'
                );
            }
        }, [props.socketUrl]);

        if (!store || !store.current) {
            store.current = initStore(
                props.connectingText,
                instanceSocket.current,
                storage,
                props.docViewer,
                props.onWidgetEvent,
                navigateAppWeb,
                emitApplicationEvent
            );
            store.current.socketRef = instanceSocket.current.marker;
            store.current.socket = instanceSocket.current;
        }

        ensureMessageHandlers(props, store.current, browserStorage, instanceSocket);

        useEffect(() => {
            log.info('backend urls:', {
                socketUrl: props.socketUrl,
                socketPath: props.socketPath,
                httpBackendUrl: props.httpBackendUrl,
            });
            store.current.dispatch(configureSocketUrl(props.socketUrl, props.socketPath));
            store.current.dispatch(configureHttpBackendUrl(props.httpBackendUrl));
        }, [props.socketUrl, props.socketPath, props.httpBackendUrl]);

        useEffect(() => {
            log.info('userInfo:', props.userInfo);

            store.current.dispatch(setUserInfoExists(!!props.userInfo));

            if (props.userInfo) {
                store.current.dispatch(setCustomerId(props.userInfo.customerId));
                store.current.dispatch(setUserId(props.userInfo.userId));
            }
        }, [props.userInfo && props.userInfo.customerId, props.userInfo && props.userInfo.userId]);

        if (
            props &&
            props.browserLocation &&
            location.current &&
            props.browserLocation.pathname != location.current.pathname
        ) {
            // log.debug('location change event', location.current);

            location.current = props.browserLocation;

            const session_id = browserStorage.getIn([SESSION_NAME, 'session_id']);

            if (
                session_id &&
                instanceSocket.current &&
                getSecondsSinceLastInteraction() <
                    ((props && props.locationChangeEventsActiveSeconds) || 0)
            ) {
                instanceSocket.current.emit('application_event', {
                    session_id,
                    event: {
                        type: APPLICATION_EVENT.LOCATION_CHANGE,
                        data: {
                            location: location.current.pathname,
                        },
                        location: location.current.pathname, // NOTE: for backwards compatibility
                    },
                });
            }
        }

        useEffect(() => {
            store.current.dispatch(setBrowserLocation(props.browserLocation));
        }, [props.browserLocation]);

        useEffect(() => {
            if (
                props.deeplink &&
                store &&
                store.current &&
                instanceSocket &&
                instanceSocket.current
            ) {
                log.debug('conveying deeplink', props.deeplink);

                store.current.dispatch(openChat());

                instanceSocket.current.emit('deeplink', {
                    deeplink: {
                        code: props.deeplink,
                    },
                });
            }
        }, [props.deeplink]);

        useEffect(() => {
            if (props.analyticsServiceUrl) {
                store.current.dispatch(setAnalyticsServiceUrl(props.analyticsServiceUrl));
            }
        }, [props.analyticsServiceUrl]);

        useEffect(() => {
            setAnalyticsPartnerId(props.partnerId);
        }, [props.partnerId]);

        useEffect(() => {
            const sessionId = browserStorage.getIn([SESSION_NAME, 'session_id']);
            if (sessionId) {
                store.current.dispatch(setSessionId(sessionId));
            }
        }, [browserStorage.getIn([SESSION_NAME, 'session_id'])]);

        useEffect(() => {
            if (props.launchIntent && instanceSocket && instanceSocket.current) {
                // NOTE: we need to distinguish the cases in which we're expecting customerId & userId but just
                //   don't have them yet (as maybe our parent does not have them yet on the first render) and
                //   when we won't get customerId & userId (as in sales bot).
                //   otherwise it could happen that we'd be sending the launchIntent multiple times (i.e. when our
                //   parent submits launchIntent, customerId & userId on multiple renders).
                if (props.userInfo) {
                    if (props.userInfo.customerId && props.userInfo.userId) {
                        log.debug(
                            'maybe conveying launch_intent with userInfo (if new session)',
                            props.userInfo.customerId,
                            props.userInfo.userId
                        );
                        instanceSocket.current.emitOnlyOnNewSession(
                            'launch_intent',
                            {},
                            props.userInfo.customerId,
                            props.userInfo.userId
                        );
                    }
                } else {
                    log.debug('maybe conveying launch_intent without userInfo (if new session)');
                    instanceSocket.current.emitOnlyOnNewSession('launch_intent', {});
                }
            }
        }, [
            props.launchIntent,
            props.userInfo && props.userInfo.customerId,
            props.userInfo && props.userInfo.userId,
        ]);

        useEffect(() => {
            props.launcherGreetingsDelayMs &&
                store?.current?.dispatch(setGreetingsDelay(props.launcherGreetingsDelayMs));
        }, [props.launcherGreetingsDelayMs]);

        // Load theme with presets
        const theme = loadTheme(props.styleTheme);

        // Apply CSS variables on mount and theme change. querySelectorAll is
        // required: in floating mode the launcher and the conversation render
        // as two separate .rw-widget-container roots, and each needs the vars.
        useEffect(() => {
            const containers = document.querySelectorAll(
                '.rw-widget-container, .rw-widget-embedded, .rw-conversation-container'
            );
            containers.forEach((container) => {
                if (container instanceof HTMLElement) {
                    applyCSSVariables(container, theme);
                }
            });
        }, [theme]);

        return (
            <Provider store={store.current}>
                <ThemeContext.Provider value={theme}>
                    <Widget
                        ref={ref}
                        initPayload={props.initPayload}
                        title={props.title ?? t('widget.title')}
                        subtitle={props.subtitle ?? t('widget.subtitle')}
                        customData={props.customData}
                        handleNewUserMessage={props.handleNewUserMessage}
                        showCloseButton={props.showCloseButton}
                        showFullScreenButton={props.showFullScreenButton}
                        hideWhenNotConnected={props.hideWhenNotConnected}
                        connectOn={props.connectOn}
                        autoClearCache={props.autoClearCache}
                        badge={props.badge}
                        embedded={props.embedded}
                        overlayMode={props.overlayMode}
                        chatWindowInsetX={props.chatWindowInsetX}
                        chatWindowInsetY={props.chatWindowInsetY}
                        disablePreviewBubble={props.disablePreviewBubble}
                        pageUrl={props.pageUrl}
                        analyticsReferrer={props.analyticsReferrer}
                        mobileFullScreenOnFocus={props.mobileFullScreenOnFocus}
                        params={props.params}
                        storage={storage}
                        inputTextFieldHint={
                            props.inputTextFieldHint ?? t('widget.inputPlaceholder')
                        }
                        openLauncherImage={props.openLauncherImage}
                        closeImage={props.closeImage}
                        customComponent={props.customComponent}
                        displayUnreadCount={props.displayUnreadCount}
                        socket={instanceSocket.current}
                        showMessageDate={props.showMessageDate}
                        customMessageDelay={props.customMessageDelay}
                        tooltipPayload={props.tooltipPayload}
                        tooltipDelay={props.tooltipDelay}
                        disableTooltips={props.disableTooltips}
                        defaultHighlightCss={props.defaultHighlightCss}
                        defaultHighlightAnimation={props.defaultHighlightAnimation}
                        defaultHighlightClassname={props.defaultHighlightClassname}
                        welcomeMessages={props.welcomeMessages ?? [t('widget.welcomeMessage')]}
                        connectingText={props.connectingText ?? t('widget.connecting')}
                    />
                    <TasksManager
                        invalidateToken={props.invalidateToken}
                        fetchTasksRetryCount={props.fetchTasksRetryCount}
                    />
                    {props.getToken && props.invalidateToken && props.getTokenIntervalMs ? (
                        <AuthtokenManager
                            getToken={props.getToken}
                            getTokenIntervalMs={props.getTokenIntervalMs}
                            invalidateToken={props.invalidateToken}
                        />
                    ) : null}
                </ThemeContext.Provider>
            </Provider>
        );
    }
);

const ConnectedWidgetWrapper = forwardRef<any, ConnectedWidgetProps>((props, ref) => {
    useEffect(() => {
        if (props.language) {
            i18next.changeLanguage(props.language);
        }
    }, [props.language]);

    return (
        <I18nextProvider i18n={i18next}>
            <ConnectedWidget ref={ref} {...props} />
        </I18nextProvider>
    );
});

(ConnectedWidgetWrapper as any).defaultProps = {
    customData: {},
    hideWhenNotConnected: true,
    autoClearCache: false,
    connectOn: 'mount',
    onSocketEvent: {},
    badge: 0,
    embedded: false,
    mobileFullScreenOnFocus: true,
    params: {
        storage: 'local',
    },
    docViewer: false,
    showCloseButton: true,
    showFullScreenButton: false,
    displayUnreadCount: false,
    showMessageDate: false,
    customMessageDelay: (message: string) => 100,
    tooltipPayload: undefined,
    tooltipDelay: 500,
    onWidgetEvent: {
        onChatOpen: () => {},
        onChatClose: () => {},
        onChatVisible: () => {},
        onChatHidden: () => {},
        onChatFullscreen: () => {},
        onChatNoFullscreen: () => {},
        onConnect: () => {},
        onDisconnect: () => {},
        onSessionConfirmed: () => {},
        onButtonClick: () => {},
        onMessageEmit: undefined,
    },
    disableTooltips: false,
    launchIntent: false,
    loglevel: 'silent',
    getToken: async () => undefined,
    invalidateToken: async () => undefined,
    styleTheme: {},
    locationChangeEventsActiveSeconds: 600,
    fetchTasksRetryCount: 3,
};

export default ConnectedWidgetWrapper;
